import { Checkbox } from '@/components/ui';
import { cn } from '@/lib/utils';
import { countryToFlagEmoji, countryKeyToIso2 } from '@/utils/country-flag-emoji';

import type { StaticTagCountryGroup } from '../../hooks/useStaticTagPropagation';
import { staticTagChipStyle } from './staticTagPropagationUtils';

interface Props {
  groups: StaticTagCountryGroup[];
  staticTagColors?: Record<string, string>;
  selectedCountries: string[];
  onToggle: (countryKey: string) => void;
  disabled?: boolean;
}

export function StaticTagCountryTable({
  groups,
  staticTagColors = {},
  selectedCountries,
  onToggle,
  disabled,
}: Props) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="grid grid-cols-[auto_minmax(0,1.2fr)_minmax(0,1.4fr)_auto] gap-x-3 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span className="pl-1">Incl.</span>
        <span>Country</span>
        <span>Matching tags</span>
        <span className="pr-1 text-right">Drafts</span>
      </div>
      <ul className="divide-y divide-border">
        {groups.map((g) => {
          const iso = countryKeyToIso2(g.countryKey) || '—';
          const isSource = Boolean(g.isBulkSourceCountry);
          const checked = isSource || selectedCountries.includes(g.countryKey);
          return (
            <li key={g.countryKey}>
              <label
                data-testid={`propagate-row-${g.countryKey}`}
                className={cn(
                  'grid grid-cols-[auto_minmax(0,1.2fr)_minmax(0,1.4fr)_auto] items-center gap-x-3 px-3 py-2.5',
                  isSource ? 'cursor-default' : 'cursor-pointer',
                  checked ? 'bg-primary/10' : 'hover:bg-muted/40',
                  disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => {
                    if (!isSource) onToggle(g.countryKey);
                  }}
                  disabled={disabled || isSource}
                  className="shrink-0"
                  aria-label={
                    isSource ? `Your selection — ${g.countryKey} is always included` : `Include ${g.countryKey}`
                  }
                />
                <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
                  <span className="text-lg leading-none" aria-hidden>
                    {countryToFlagEmoji(g.countryKey)}
                  </span>
                  <span className="min-w-0 truncate" title={g.countryKey}>
                    <span className="text-muted-foreground">{iso}</span>
                    <span className="ml-1.5 font-normal capitalize text-foreground/90">
                      {g.countryKey.replace(/_/g, ' ')}
                    </span>
                  </span>
                  {isSource ? (
                    <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground">
                      Your selection
                    </span>
                  ) : null}
                </span>
                <span className="flex min-w-0 flex-wrap gap-1">
                  {g.tags.map((t) => {
                    const hex = staticTagColors[t.tagName];
                    const chipStyle = staticTagChipStyle(hex);
                    return (
                      <span
                        key={t.tagName}
                        className={cn(
                          'inline-flex max-w-full items-center truncate rounded-md border px-1.5 py-0.5 text-[11px]',
                          chipStyle
                            ? 'font-medium'
                            : 'border-border/80 bg-muted/50 text-foreground',
                        )}
                        style={chipStyle}
                        title={t.tagName}
                      >
                        {t.tagName}
                        <span
                          className={cn(
                            'ml-1 tabular-nums',
                            chipStyle ? 'opacity-80' : 'text-muted-foreground',
                          )}
                        >
                          ({t.draftCount})
                        </span>
                      </span>
                    );
                  })}
                </span>
                <span className="shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                  {g.totalDrafts}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
